// client/src/pages/Admin/ResourceManager/utils/resourceHelpers.ts
import {
    Resource,
    ResourceCategoryLabels,
    ResourceStatus,
    ResourceParentCategoryLabels,
    ResourceParentCategoryMapping
} from '../../../../shared/models/Resource';
import React from 'react';
import {
    CheckCircle2,
    FileEdit,
    Clock3,
    Archive,
    FileText,
    LucideIcon
} from 'lucide-react';

// ============================================
// CATEGORY HELPERS
// ============================================

export const getParentCategory = (category: string): string => {
    if (!category) return '';
    for (const [parent, children] of Object.entries(ResourceParentCategoryMapping)) {
        if (children.includes(category as any)) {
            return parent;
        }
    }
    return '';
};

export const getParentCategoryDisplay = (category: string): string => {
    if (!category) return '';
    const parent = getParentCategory(category);
    if (!parent) return '';
    const label = ResourceParentCategoryLabels[parent as keyof typeof ResourceParentCategoryLabels];
    return label || parent;
};

export const getCategoryDisplay = (category: string): string => {
    if (!category) return 'Uncategorized';
    const label = ResourceCategoryLabels[category as keyof typeof ResourceCategoryLabels];
    return label || category;
};

// ============================================
// STATUS HELPERS
// ============================================

export const getStatusIcon = (status: ResourceStatus): LucideIcon => {
    switch (status) {
        case ResourceStatus.PUBLISHED:
            return CheckCircle2;
        case ResourceStatus.DRAFT:
            return FileEdit;
        case ResourceStatus.UNDER_REVIEW:
            return Clock3;
        case ResourceStatus.ARCHIVED:
            return Archive;
        default:
            return FileText;
    }
};

export const renderStatusIcon = (status: ResourceStatus, size: number = 14): React.ReactElement => {
    const Icon = getStatusIcon(status);
    return <Icon size={size} />;
};

export const getStatusColor = (status: ResourceStatus): string => {
    switch (status) {
        case ResourceStatus.PUBLISHED:
            return 'status-published';
        case ResourceStatus.DRAFT:
            return 'status-draft';
        case ResourceStatus.UNDER_REVIEW:
            return 'status-review';
        case ResourceStatus.ARCHIVED:
            return 'status-archived';
        default:
            return 'status-default';
    }
};

export const formatStatusLabel = (status: string): string => {
    if (!status) return '';
    return status
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c: string) => c.toUpperCase());
};

// ============================================
// TEXT HELPERS
// ============================================

export const truncate = (text: string, length = 140): string => {
    if (!text) return '';
    return text.length > length
        ? text.substring(0, length) + '...'
        : text;
};

export const formatDate = (date: Date | string): string => {
    if (!date) return '';
    try {
        return new Date(date).toLocaleDateString(undefined, {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    } catch {
        return '';
    }
};